import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, GoogleLogo, LockKey, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const errorText = (err) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join(" ");
  return "Authentication failed. Please try again.";
};

export function EkamAuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      navigate("/ekam/login", { replace: true });
      return;
    }
    api.post("/ekam/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        window.history.replaceState(null, "", "/ekam");
        navigate("/ekam", { replace: true, state: { user: data.user } });
      })
      .catch((err) => {
        toast.error(errorText(err));
        navigate("/ekam/login", { replace: true });
      });
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen bg-[#041718] text-slate-100 flex items-center justify-center" data-testid="ekam-auth-callback">
      <div className="rounded-3xl border border-teal-500/20 bg-[#0a2527]/80 p-10 backdrop-blur-xl text-center">
        <div className="mx-auto h-12 w-12 rounded-full border border-cyan-400/40 border-t-cyan-300 animate-spin" />
        <p className="mt-6 text-sm text-slate-400">Establishing your secure Ekam session…</p>
      </div>
    </div>
  );
}

export default function EkamLogin() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [organization, setOrganization] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/ekam/auth/me").then(() => navigate("/ekam", { replace: true })).catch(() => {});
  }, [navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/ekam`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = mode === "register"
        ? { name, organization, email, password }
        : { email, password, ...(mfaRequired ? { otp } : {}) };
      const { data } = await api.post(`/ekam/auth/${mode}`, payload);
      if (data.mfa_required) {
        setMfaRequired(true);
        toast.info("Enter the authenticator-app code to finish signing in.");
        return;
      }
      toast.success(`Welcome to Ekam, ${data.user.name}.`);
      navigate("/ekam", { replace: true, state: { user: data.user } });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#041718] text-slate-100 overflow-hidden relative" data-testid="ekam-login-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(6,182,212,0.16),transparent_28%),radial-gradient(circle_at_80%_0%,rgba(20,184,166,0.12),transparent_30%)]" />
      <div className="relative mx-auto grid min-h-screen max-w-7xl gap-10 px-6 py-8 lg:grid-cols-12 lg:px-10">
        <div className="flex flex-col justify-between lg:col-span-6 py-8">
          <Link to="/" data-testid="ekam-login-home-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 transition-colors">
            <ArrowLeft size={16} /> Back to Anxya
          </Link>
          <div className="max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-cyan-300">Ekam</p>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl font-light tracking-tighter text-white leading-[0.95]">
              One intelligence workspace.
              <span className="block text-slate-500">Human-controlled.</span>
              <span className="block italic text-cyan-300">Credit-governed.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-slate-400 leading-relaxed">
              Sign in with Gmail or your corporate identity, enable MFA, and run governed intelligence only when credits are available.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 text-xs text-slate-500">
            {["Tenant isolated", "MFA ready", "Audited runs"].map((item) => (
              <div key={item} className="rounded-full border border-teal-500/15 px-4 py-2">{item}</div>
            ))}
          </div>
        </div>

        <div className="flex items-center lg:col-span-6">
          <div className="w-full max-w-xl mx-auto rounded-[2rem] border border-teal-500/20 bg-[#0a2527]/80 p-6 sm:p-10 shadow-2xl shadow-black/30 backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-400/10 text-cyan-300"><ShieldCheck size={24} weight="duotone" /></span>
              <div>
                <h2 className="font-display text-2xl font-medium text-white">Access Ekam</h2>
                <p className="text-sm text-slate-400">Google, corporate ID and TOTP MFA.</p>
              </div>
            </div>

            <Button type="button" onClick={googleLogin} data-testid="ekam-google-login-btn" className="mt-8 h-13 w-full rounded-full bg-white text-sm font-semibold text-slate-950 hover:bg-cyan-50">
              <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Gmail / Google
            </Button>

            <div className="my-7 flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-slate-500">
              <span className="h-px flex-1 bg-teal-500/15" /> Corporate ID <span className="h-px flex-1 bg-teal-500/15" />
            </div>

            <div className="grid grid-cols-2 rounded-full border border-teal-500/15 bg-[#041718] p-1">
              {["login", "register"].map((item) => (
                <button key={item} type="button" onClick={() => { setMode(item); setError(""); setMfaRequired(false); }} data-testid={`ekam-auth-mode-${item}`} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${mode === item ? "bg-cyan-400 text-slate-950" : "text-slate-400 hover:text-white"}`}>
                  {item === "login" ? "Sign in" : "Create account"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="ekam-auth-form">
              {mode === "register" && (
                <>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="ekam-name-input" placeholder="Full name" className="h-12 rounded-xl border-teal-500/15 bg-[#041718] text-white placeholder:text-slate-500" />
                  <Input value={organization} onChange={(e) => setOrganization(e.target.value)} data-testid="ekam-organization-input" placeholder="Organization" className="h-12 rounded-xl border-teal-500/15 bg-[#041718] text-white placeholder:text-slate-500" />
                </>
              )}
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="ekam-email-input" placeholder={mode === "register" ? "Corporate email" : "Email"} className="h-12 rounded-xl border-teal-500/15 bg-[#041718] text-white placeholder:text-slate-500" />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} data-testid="ekam-password-input" placeholder="Password" className="h-12 rounded-xl border-teal-500/15 bg-[#041718] text-white placeholder:text-slate-500" />
              {mfaRequired && (
                <Input value={otp} onChange={(e) => setOtp(e.target.value)} required inputMode="numeric" data-testid="mfa-totp-input" placeholder="6-digit authenticator code" className="h-12 rounded-xl border-cyan-400/40 bg-[#041718] text-white placeholder:text-slate-500" />
              )}
              {error && <p data-testid="ekam-auth-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="ekam-auth-submit-btn" className="h-13 w-full rounded-full bg-cyan-400 text-sm font-bold text-slate-950 hover:bg-cyan-300">
                {loading ? "Securing session…" : mfaRequired ? "Verify MFA & Sign In" : mode === "login" ? "Sign In" : "Create Corporate Account"} <ArrowUpRight size={16} className="ml-2" />
              </Button>
            </form>

            <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-slate-500">
              <LockKey size={14} className="mt-0.5 shrink-0 text-cyan-300" /> Corporate email/password accounts require a non-free company domain. Gmail users should use Google sign-in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
